import React from 'react';
import PropTypes from 'prop-types';
import { Form, Input, Select } from 'antd';
import { Info } from '@phosphor-icons/react';
import DebounceSelect from './DebounceSelect';

const FieldLabel = ({ htmlFor, required, children }) => (
  <label className="assign-record-modal__label" htmlFor={htmlFor}>
    {children}
    {required && <span className="assign-record-modal__required" aria-hidden="true">*</span>}
  </label>
);

FieldLabel.propTypes = {
  htmlFor: PropTypes.string.isRequired,
  required: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

FieldLabel.defaultProps = {
  required: false,
};

// The body shared by the single-record and bulk assign modals. Presentational
// only: each modal keeps its own state, submit flow and footer.
const AssignRecordFields = ({
  formId,
  onFinish,
  summary,
  staysOpenNote,
  documentStatuses,
  selectedDocumentStatus,
  onDocumentStatusChange,
  assignee,
  fetchUserList,
  onAssigneeChange,
  note,
  onNoteChange,
}) => (
  <div className="assign-record-modal__body">
    {summary && <div className="assign-record-modal__summary">{summary}</div>}
    {staysOpenNote && (
      <div className="assign-record-modal__callout" role="note">
        <Info size={18} weight="fill" />
        <span>{staysOpenNote}</span>
      </div>
    )}
    <Form id={formId} onFinish={onFinish} className="assign-record-modal__form">
      <div className="assign-record-modal__row">
        <div className="assign-record-modal__field">
          <FieldLabel htmlFor={`${formId}-assignee`} required>Assign to</FieldLabel>
          <DebounceSelect
            id={`${formId}-assignee`}
            showSearch
            allowClear
            value={assignee}
            placeholder="Search Medsender users"
            fetchOptions={fetchUserList}
            onChange={onAssigneeChange}
          />
        </div>
        <div className="assign-record-modal__field">
          <FieldLabel htmlFor={`${formId}-status`} required>Document status</FieldLabel>
          <Select
            id={`${formId}-status`}
            onChange={onDocumentStatusChange}
            placeholder="Select a status"
            value={selectedDocumentStatus}
          >
            {documentStatuses.map((docStatus) => (
              <Select.Option key={docStatus.id} value={docStatus.id}>{docStatus.name}</Select.Option>
            ))}
          </Select>
        </div>
      </div>
      <div className="assign-record-modal__field">
        <FieldLabel htmlFor={`${formId}-note`}>
          Note
          <span className="assign-record-modal__optional"> (optional)</span>
        </FieldLabel>
        {/* Controlled: `isLoading` unmounts this form during the access
            confirmation round trip, so an uncontrolled textarea would remount
            empty while `note` still holds text the user can no longer see. */}
        <Input.TextArea
          id={`${formId}-note`}
          onChange={(event) => onNoteChange(event.target.value)}
          placeholder="Add context for the assignee"
          rows={3}
          value={note}
        />
      </div>
    </Form>
  </div>
);

AssignRecordFields.propTypes = {
  formId: PropTypes.string.isRequired,
  onFinish: PropTypes.func,
  summary: PropTypes.node,
  staysOpenNote: PropTypes.node,
  documentStatuses: PropTypes.array.isRequired,
  selectedDocumentStatus: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onDocumentStatusChange: PropTypes.func.isRequired,
  assignee: PropTypes.object,
  fetchUserList: PropTypes.func.isRequired,
  onAssigneeChange: PropTypes.func.isRequired,
  note: PropTypes.string.isRequired,
  onNoteChange: PropTypes.func.isRequired,
};

AssignRecordFields.defaultProps = {
  onFinish: undefined,
  summary: null,
  staysOpenNote: null,
  selectedDocumentStatus: null,
  assignee: undefined,
};

export default AssignRecordFields;
